import { CometChatCalls } from '@cometchat/calls-sdk-javascript';
import { CometChatUIKit } from '@cometchat/chat-uikit-react';
import { onAuthStateChanged } from 'firebase/auth';
import { useEffect, useState } from 'react';
import './App.css';
import './material.css';
import { COMETCHAT_CONSTANTS } from './AppConstants';
import HistoryScreen from './History';
import Groups from './Groups';
import Home from './Home';
import LoginWithFirebase from './LoginWithFirebase';
import Users from './Users';
import { auth, signOutFirebase } from './firebase';
import { AppShell } from './components/AppShell/AppShell';
import { CallProvider } from './calls/CallProvider';
import { useSystemTheme } from './hooks/useSystemTheme';
import { initCallsSDK } from './utils/init-calls-sdk';

const isE2ETest = import.meta.env.VITE_E2E_TEST === 'true';

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isFirebaseReady, setIsFirebaseReady] = useState(false);
  const [isSessionActive, setIsSessionActive] = useState(false);
  // PRE-0 wants both themes chosen by the system. That works BEFORE sign-in
  // and not yet after it, because Connect Web is painted by two CSS variable
  // families and only one follows a theme:
  //
  //   --cometchat-*        (Chat UIKit, 210 decls)  light: bg #FFFFFF / text #141414  -- theme-aware
  //   --cometchat-calls-*  (Calls SDK,  160 decls)  always: bg #141414 / text #ffffff -- DARK ONLY
  //
  // The loading and sign-in screens are drawn entirely from Connect's own
  // tokens (no --cometchat-calls-* left on them), so they follow the OS like
  // Air's sign-in does. Past sign-in, Calls-SDK-painted surfaces dominate and a
  // half-light app is worse than a consistently dark one, so the app stays
  // pinned dark there until the Calls SDK gains a light palette -- plan PRE-9.
  const systemTheme = useSystemTheme();
  const theme: 'light' | 'dark' = isLoggedIn ? 'dark' : systemTheme;

  // Mirror the resolved theme onto <html> so tokens.css resolves the SAME way.
  // data-theme on a div drives the CometChat UIKit; our custom properties live
  // on :root and would otherwise keep following prefers-color-scheme on their
  // own -- which rendered our screens white inside a dark app.
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  useEffect(() => {
    const initSDK = async () => {
      try {
        const { error } = await initCallsSDK();

        if (error) {
          console.error('CometChat SDK initialization failed:', error);
          return;
        }

        setIsInitialized(true);
        console.log('Calls SDK initialized successfully');
      } catch (error) {
        console.error('CometChat SDK initialization failed:', error);
      }
    };

    initSDK();
  }, []);

  // In e2e mode, bypass Firebase and login directly via CometChat SDK
  useEffect(() => {
    if (!isE2ETest || !isInitialized) return;

    const e2eLogin = async () => {
      try {
        const uid =
          new URLSearchParams(window.location.search).get('e2e_uid') ||
          'cometchat-uid-1';
        await CometChatCalls.login(uid, COMETCHAT_CONSTANTS.AUTH_KEY);
        // The Chat SDK too, as the Google / guest sign-in does (LoginWithFirebase): Users,
        // Groups, the meeting chat and incoming calls all go through it, and without a chat
        // login every one of those requests went out with no auth token (401).
        const chatUser = await CometChatUIKit.getLoggedinUser();
        if (chatUser?.getUid() !== uid) {
          if (chatUser) await CometChatUIKit.logout();
          await CometChatUIKit.login(uid);
        }
        setIsLoggedIn(true);
      } catch (error) {
        console.error('E2E auto-login failed:', error);
      }
      setIsFirebaseReady(true);
    };

    e2eLogin();
  }, [isInitialized]);

  // Listen for Firebase authentication state changes (skip in e2e mode)
  useEffect(() => {
    if (isE2ETest) return;

    const unsubscribe = onAuthStateChanged(auth, user => {
      if (user) {
        const cometChatUser = CometChatCalls.getLoggedInUser();
        if (cometChatUser) {
          setIsLoggedIn(true);
        }
      } else {
        setIsLoggedIn(false);
      }
      setIsFirebaseReady(true);
    });

    return () => unsubscribe();
  }, []);

  const handleLoginSuccess = () => {
    setIsLoggedIn(true);
  };

  const handleLogout = async () => {
    try {
      await CometChatCalls.logout();
      await CometChatUIKit.logout();
      await signOutFirebase();
      setIsLoggedIn(false);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Show loading while SDK is initializing
  if (!isInitialized || !isFirebaseReady) {
    return (
      <div className="container-app" data-theme={theme}>
        <div className="card loading-container">
          <div className="loading-spinner" />
          <h2 className="loading-title">Initializing CometChat</h2>
          <p className="loading-subtitle">
            Setting up your communication experience...
          </p>
        </div>
      </div>
    );
  }

  // Show Firebase login if not logged in
  if (!isLoggedIn) {
    return (
      <div data-theme={theme}>
        <LoginWithFirebase onLoginSuccess={handleLoginSuccess} />
      </div>
    );
  }

  // Main App UI when logged in
  const meeting = (
    <Home onLogout={handleLogout} onSessionActiveChange={setIsSessionActive} />
  );

  // The list pane's tabs, picked by path in the shell. The meeting is not one
  // of them: the shell keeps it mounted beside the lists on wide screens and
  // shows it for every non-list path on narrow ones (/, /meeting, invite links
  // /<sessionId>). On wide screens those same paths show History, the default
  // tab.
  const lists = {
    history: <HistoryScreen onLogout={handleLogout} />,
    users: <Users />,
    groups: <Groups />,
  };

  return (
    <div data-theme={theme} style={{ height: '100%', width: '100%' }}>
      {/* Signed-in only: ringing needs a logged-in Chat SDK to receive
          calls and a logged-in Calls SDK to place them. */}
      <CallProvider inCall={isSessionActive}>
        <AppShell
          lists={lists}
          meeting={meeting}
          inCall={isSessionActive}
          onLogout={handleLogout}
        />
      </CallProvider>
    </div>
  );
}

export default App;
